import { type FC, useEffect } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useToken } from "@packsize/authentication";

import { baseRoutes } from "@/api/base-routes";
import { useSignalRHub } from "@/hooks/use-signalr-hub";
import type { ErrorsAndWarningsProps, MachineFaultDisplay, MachineFaultType } from "@/schemas";

const websocketUrl = `${baseRoutes.x6}/hubs/x6`;
const alarmsChangedEventName = "AlarmsChanged";
const warningsChangedEventName = "WarningsChanged";

type MachineEvent = {
	code?: number;
};

type SignalRResponse = {
	machineId: string;
	events: MachineEvent[];
};

function createDisplayItems(
	machineId: string,
	data: MachineEvent[] | null | undefined,
	severity: MachineFaultType,
	intl: IntlShape,
): MachineFaultDisplay[] {
	if (data === undefined || data === null) {
		return [];
	}

	return data.map((event) => {
		const code = event.code ?? 0;
		const translationKey = `X6 ${severity} ${code}`;

		return {
			machineId,
			title: intl.formatMessage({ id: translationKey, defaultMessage: "{translationKey}" }, { translationKey }),
			image: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/fallback.svg`,
			type: severity,
		};
	});
}

interface X6MachineErrorsAndWarningsProps {
	readonly machineId: string;
	readonly onErrorsChanged: ErrorsAndWarningsProps["onErrorsChanged"];
	readonly onWarningsChanged: NonNullable<ErrorsAndWarningsProps["onWarningsChanged"]>;
}

const X6MachineErrorsAndWarnings = ({
	machineId,
	onErrorsChanged,
	onWarningsChanged,
}: X6MachineErrorsAndWarningsProps) => {
	const intl = useIntl();
	const { bearerToken } = useToken();
	const { hubConnection, hubConnectionEstablished, invoke, subscribe, unsubscribe } = useSignalRHub({
		hubUrl: websocketUrl,
		token: bearerToken,
	});

	useEffect(() => {
		if (hubConnectionEstablished === false) {
			return;
		}

		const currentMachineIds = [machineId];

		async function subscribeToGroups(abortController: AbortController): Promise<void> {
			const [alarms] = (await invoke<SignalRResponse[]>("joinAlarmsChangedGroup", currentMachineIds)) ?? [];

			if (alarms && !abortController.signal.aborted && alarms.machineId === machineId) {
				onErrorsChanged(machineId, createDisplayItems(machineId, alarms.events, "Error", intl));
			}

			const [warnings] = (await invoke<SignalRResponse[]>("joinWarningsChangedGroup", currentMachineIds)) ?? [];

			if (warnings && !abortController.signal.aborted && warnings.machineId === machineId) {
				onWarningsChanged(machineId, createDisplayItems(machineId, warnings.events, "Warning", intl));
			}
		}

		const abortController = new AbortController();

		void subscribeToGroups(abortController);

		return () => {
			abortController.abort();

			void invoke("leaveAlarmsChangedGroup", currentMachineIds);
			void invoke("leaveWarningsChangedGroup", currentMachineIds);
		};
	}, [hubConnectionEstablished, intl, invoke, machineId, onErrorsChanged, onWarningsChanged]);

	useEffect(() => {
		if (hubConnection === undefined) {
			return;
		}

		const handleAlarmsChanged = (...args: unknown[]) => {
			const [data] = args as [string];

			let eventMachineId: string | undefined;

			try {
				const activeAlarms = JSON.parse(data) as SignalRResponse;

				eventMachineId = activeAlarms.machineId;

				if (eventMachineId === undefined || eventMachineId !== machineId) {
					return;
				}

				onErrorsChanged(eventMachineId, createDisplayItems(eventMachineId, activeAlarms.events, "Error", intl));
			} catch (error) {
				console.error(
					`There was an error processing ${alarmsChangedEventName} SignalR event for machine: ${eventMachineId}`,
					error,
				);
			}
		};

		subscribe(alarmsChangedEventName, handleAlarmsChanged);

		return () => {
			unsubscribe(alarmsChangedEventName, handleAlarmsChanged);
		};
	}, [hubConnection, intl, machineId, onErrorsChanged, subscribe, unsubscribe]);

	useEffect(() => {
		if (hubConnection === undefined) {
			return;
		}

		const handleWarningsChanged = (...args: unknown[]) => {
			const [data] = args as [string];

			let eventMachineId: string | undefined;

			try {
				const activeWarnings = JSON.parse(data) as SignalRResponse;

				eventMachineId = activeWarnings.machineId;

				if (eventMachineId === undefined || eventMachineId !== machineId) {
					return;
				}

				onWarningsChanged(eventMachineId, createDisplayItems(eventMachineId, activeWarnings.events, "Warning", intl));
			} catch (error) {
				console.error(
					`There was an error processing ${warningsChangedEventName} SignalR event for machine: ${eventMachineId}`,
					error,
				);
			}
		};

		subscribe(warningsChangedEventName, handleWarningsChanged);

		return () => {
			unsubscribe(warningsChangedEventName, handleWarningsChanged);
		};
	}, [hubConnection, intl, machineId, onWarningsChanged, subscribe, unsubscribe]);

	return null;
};

export const X6ErrorsAndWarnings: FC<Required<ErrorsAndWarningsProps>> = ({
	machines,
	onErrorsChanged,
	onWarningsChanged,
}) => {
	return (
		<>
			{machines
				.filter(({ machineType: configuredMachineType }) => configuredMachineType === "X6")
				.map(({ machineId }) => (
					<X6MachineErrorsAndWarnings
						key={machineId}
						machineId={machineId}
						onErrorsChanged={onErrorsChanged}
						onWarningsChanged={onWarningsChanged}
					/>
				))}
		</>
	);
};
